import React from 'react';
import { Formik, Field, Form } from 'formik';
import emailjs from 'emailjs-com';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { motion } from 'framer-motion';
import AnimatedButton from '../../utilities/AnimatedButton';
import { HiOutlineMail } from 'react-icons/hi';

export default function ContactSection() {
  const validate = (values) => {
    const errors = {};
    if (
      values.user_email &&
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.user_email)
    ) {
      errors.user_email = 'Correo inválido';
    }
    return errors;
  };

  const entryTransition = { duration: 0.6, ease: 'easeOut' };
  const springTransition = { type: 'spring', stiffness: 120, damping: 20 };

  const initialValues = {
    user_name: '',
    user_email: '',
    message: '',
  };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    const serviceId = 'service_yrrg5qe';
    const templateId = 'template_ukf8ue9';
    const userId = 'J-orKvPm_nXmqd9JY';

    emailjs.send(serviceId, templateId, values, userId).then(
      (response) => {
        resetForm();
        setSubmitting(false);
        toast.success('Correo enviado con éxito');
      },
      (error) => {
        setSubmitting(false);
        toast.error(
          'Hubo un error al enviar el correo. Por favor, inténtelo de nuevo.'
        );
      }
    );
  };

  // Variantes para animación escalonada de columnas
  const bgVariants = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { ...entryTransition } },
  };
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { ...entryTransition, delay: 0.2 } },
  };
  const columnsVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.25,
        delayChildren: 0.5,
      },
    },
  };
  const columnItemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { ...entryTransition } },
  };

  return (
    <>
      <section className="relative py-12 px-4 sm:px-8 md:px-24">
        {/* Imagen de fondo animada */}
        <motion.div
          className="absolute inset-0 h-1/2 bg-cover bg-center shadow-3xl"
          style={{
            backgroundImage:
              "url('https://www.metalocus.es/sites/default/files/styles/mopis_fullslider_desktop/public/lead-images/metalocus_20-edificios-debes-visitar-ny_01_p.jpg?itok=q528kHPW')",
          }}
          variants={bgVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        />
        {/* Contenedor principal animado */}
        <motion.div
          className="relative mx-auto px-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <motion.div
            className="bg-white shadow-2xl rounded-lg p-4 sm:p-8 md:p-12 lg:flex lg:items-center"
            variants={columnsVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Columna izquierda: título, info y formulario */}
              <motion.div
                className="flex flex-col justify-center p-2 md:p-4 lg:p-8"
                variants={columnItemVariants}
              >
                {/* Título */}
                <div className="flex flex-col items-center justify-center pt-2 pb-6">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl tracking-wide font-bold font-heading leading-none text-gray-700 text-center">
                    Contáctenos
                  </h2>
                  {/* Línea animada */}
                  <motion.div
                    className="h-1 w-36 sm:w-40 mt-4 rounded-full bg-gradient-to-r from-customBlue via-customYellow/50 to-customBlue"
                    style={{ backgroundSize: '200% 100%' }}
                    animate={{ backgroundPosition: ['0% 0', '100% 0', '0% 0'] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  />
                </div>
                {/* Texto de ayuda */}
                <p className="mt-4 mb-6 text-sm md:text-lg text-gray-400 text-center lg:text-left leading-relaxed">
                  Estamos aquí para ayudar. Por favor, complete el formulario a
                  continuación y nos pondremos en contacto con usted lo antes
                  posible.
                </p>
                {/* Formulario animado */}
                <div>
                  <Formik
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting, values, errors, touched }) => {
                      const allFilled =
                        values.user_name &&
                        values.user_email &&
                        values.message &&
                        Object.keys(errors).length === 0;
                      return (
                        <Form>
                          <div className="mb-4">
                            <label
                              htmlFor="name"
                              className="block text-sm md:text-md text-gray-700 mb-2 font-semibold"
                            >
                              Nombre
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="user_name"
                              autoComplete="off"
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-customBlue ${
                                errors.user_name && touched.user_name
                                  ? 'border-red-500'
                                  : ''
                              }`}
                            />
                            {errors.user_name && touched.user_name && (
                              <div className="text-red-500 text-xs mt-1">
                                {errors.user_name}
                              </div>
                            )}
                          </div>
                          <div className="mb-4">
                            <label
                              htmlFor="email"
                              className="block text-sm md:text-md text-gray-700 mb-2 font-semibold"
                            >
                              Correo electrónico
                            </label>
                            <Field
                              type="email"
                              id="email"
                              name="user_email"
                              autoComplete="off"
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none ${
                                values.user_email
                                  ? errors.user_email
                                    ? 'border-red-500'
                                    : 'border-customBlue focus:ring-2 focus:ring-customBlue'
                                  : 'border-gray-300'
                              }`}
                            />
                          </div>
                          <div className="mb-4">
                            <label
                              htmlFor="message"
                              className="block text-sm md:text-md text-gray-700 mb-2 font-semibold"
                            >
                              Mensaje
                            </label>
                            <Field
                              as="textarea"
                              id="message"
                              name="message"
                              rows="4"
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-customBlue ${
                                errors.message && touched.message
                                  ? 'border-red-500'
                                  : ''
                              }`}
                            />
                            {errors.message && touched.message && (
                              <div className="text-red-500 text-xs mt-1">
                                {errors.message}
                              </div>
                            )}
                          </div>
                          <div className="flex justify-center">
                            <motion.div
                              className="flex justify-center mt-2 sm:mt-4"
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={springTransition}
                            >
                              <AnimatedButton
                                label="Enviar"
                                icon={HiOutlineMail}
                                disabled={!allFilled || isSubmitting}
                              />
                            </motion.div>
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </motion.div>
              {/* Columna derecha: imagen actual */}
              <motion.div
                className="h-full w-full flex items-stretch justify-end"
                variants={columnItemVariants}
              >
                <div className="hidden lg:flex h-full w-full items-stretch justify-end">
                  <img
                    src="https://images.pexels.com/photos/8866802/pexels-photo-8866802.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                    alt="Contact"
                    className="object-cover w-full h-full rounded-none lg:rounded-r-lg shadow-2xl"
                    style={{ minHeight: '100%', minWidth: '100%' }}
                  />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </section>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Bounce}
      />
    </>
  );
}
